import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import { FormattedMessage } from 'react-intl';
import { parse, stringify } from 'qs';
import GreyBox from '@/browser/GreyBox';
import SearchBar from '@/containers/Search/SearchBar';
import ProductCard from '@/components/Product/ProductCard';
import ProductSearchTabs from '@/components/Shop/ProductSearchTabs';
import RestQueryHandler from '@/components/GraphQL/RestQueryHandler';
import { Shopcontainer, ProductGrid } from './ShopStylers';
import { NormalButton } from '@/components/Buttons/Button';
import {
  gaCreateEvent,
  gaGetECommerce,
  gaSetupCustomPageviews
} from '@/utils/gaUtils';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { store } from '@/store/reducers/auth';

const PATH_PRODUCTS = '/woocommerce/wc/v3/products';

const SearchResultSubtitle = styled.div`
  margin-bottom: 41px;
  padding-top: 22px;
  font-family: ${(props) => props.theme.fonts.secondary};
  font-size: 22px;
`;

const LoadmoreContainer = styled.div`
  padding-top: 32px;
  padding-bottom: 32px;
  text-align: center;
  margin-bottom: 60px;
`;

/**
 * Products component for displaying a list of products.
 * @param {Object} props - The component's props.
 * @param {Object} props.location - The location object from React Router.
 * @param {Object} props.auth - The authentication object from Redux.
 */
const Products = ({ location, auth, history }) => {
  const [page, setPage] = useState(1);
  const [perPage] = useState(9);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [fetchmore, setFetchmore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    window.scroll({ top: 0, left: 0 });
  }, []);

  useEffect(() => {
    if (location.search !== searchQuery) {
      setPage(1);
      setSearchQuery(location.search);
    }
  }, [location.search]);
  /**
   * Fetch more products and update the page.
   */
  const fetchMore = () => {
    setPage(page + 1);
    setFetchmore(true);
  };

  /**
   * Handle the completion of fetching products.
   * @param {Array} items - The fetched products.
   * @param {Object} category - The selected category.
   */
  const onCompleted = (items, category) => {
    const categoryName = category ? category.name : 'kaikki';

    // Create Google Analytics events
    gaCreateEvent(
      gaSetupCustomPageviews(
        {
          title: 'Kauppa',
          categories: { nodes: [category || { slug: 'kaikki' }] }
        },
        location,
        auth
      )
    );

    if (!items) return;

    gaCreateEvent({
      event: 'view_item_list',
      ecommerce: gaGetECommerce({
        item_list_name: categoryName,
        items: items
      })
    });

    setFetchmore(false);
    setHasNextPage(items.length === perPage);
  };

  const { query } = location.search ? parse(location.search.substring(1)) : {};
  const { category } = parse(location.search.substring(1)) ?? '';

  return (
    <Shopcontainer>
      <Helmet title="Kauppa - Kuluttaja" />
      <FormattedMessage id="page.shop.title" tagName="h1" />
      <GreyBox>
        <FormattedMessage id="page.shop.search" tagName="h2" />
        <SearchBar
          param="query"
          placeholder="page.shop.placeholder"
          displayButton
        />
      </GreyBox>
      <RestQueryHandler
        path={`${PATH_PRODUCTS}/categories?per_page=60&hide_empty=true`}
        onCompleted={(data) => setPage(1)}
      >
        {(productCategories) => (
          <>
            <ProductSearchTabs
              categories={productCategories}
              selectedCategoryUrl={category}
              didClick={(e) => {
                setPage(1);
                setHasNextPage(false);
                const queryParams = parse(location.search.substring(1));
                if (e?.name) {
                  // make sure not to loose query params
                  queryParams.category = e.name.toLowerCase();
                  history.push({ search: stringify(queryParams) });
                } else {
                  queryParams.category = '';
                  history.push({ search: stringify(queryParams) });
                }
              }}
            >
              {(selected) => (
                <RestQueryHandler
                  path={`${PATH_PRODUCTS}?status=publish&per_page=${perPage}&search=${
                    query || ''
                  }&category=${selected ? selected.id : ''}&page=${page}`}
                  fetchmore={fetchmore}
                  onCompleted={(data) => onCompleted(data, selected)}
                >
                  {(products) => (
                    <>
                      <SearchResultSubtitle>
                        {selected ? (
                          <p>{selected.name}</p>
                        ) : (
                          <FormattedMessage
                            id="page.search.result.subtitle.all"
                            values={{
                              count: products?.length,
                              text: query
                            }}
                            tagName="p"
                          />
                        )}
                      </SearchResultSubtitle>
                      <ProductGrid>
                        {products &&
                          products.map((product) => (
                            <ProductCard key={product.id} product={product} />
                          ))}
                      </ProductGrid>
                    </>
                  )}
                </RestQueryHandler>
              )}
            </ProductSearchTabs>
            {hasNextPage && (
              <LoadmoreContainer>
                <NormalButton onClick={() => fetchMore()}>
                  <FormattedMessage id="bodybuilder.loadmore" />
                </NormalButton>
              </LoadmoreContainer>
            )}
          </>
        )}
      </RestQueryHandler>
    </Shopcontainer>
  );
};

Products.propTypes = {
  /**
   * The location object from React Router.
   */
  location: PropTypes.object.isRequired,
  /**
   * The authentication object from Redux.
   */
  auth: PropTypes.object.isRequired
};

export default withRouter(
  connect((state) => ({ auth: state.auth }), { store })(Products)
);
